/* eslint-disable react-hooks/exhaustive-deps */

import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	colors,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid,
	LinearProgress,
	MenuItem,
	Select,
	type SelectChangeEvent,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import type React from "react";
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { BsUiChecks } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../features/auth/hooks/use-auth";
import type { CompanyProps } from "../../../features/company/types/company";
import ResponseButton from "../../../features/query/components/response-button";
import type {
	ListQueryProps,
	QueryProps,
	ResponseQueryProps,
	RetrieveQueryProps,
} from "../../../features/query/types/query";
import InputDatePicker from "../../../shared/components/input-date-picker";
import { Paginator } from "../../../shared/components/paginator";
import SearchWithFilter, {
	isSearchTermValid,
	SearchFilterOption,
} from "../../../shared/components/search-with-filter";
import ApiAutocompleteFilter from "../../../features/api/components/api-autocomplete-filter";
import { onlyDigits } from "../../../shared/utils/only-digits";
import { onlyAlphanumeric } from "../../../shared/utils/only-alphanumeric";
import { MaskCNPJ, MaskCPF } from "../../../shared/utils/mask";
import { useCore } from "../../../shared/hooks/use-core";
import { useDebounce } from "../../../shared/hooks/use-debounce";
import useWindowSize from "../../../shared/hooks/use-window-size";
import * as api from "../../../shared/lib/api";
import { StatusColor } from "../../../shared/styles/theme";
import { getResponseContent } from "../../../shared/utils/api-response";

type CsvExportStatus = "pending" | "processing" | "completed" | "error";
type CsvExportPhase = "idle" | "starting" | CsvExportStatus | "downloading";
type ExportReportType = "extrato" | "faturamento";

const BILLING_OPTIONAL_COLUMNS = [
	{ value: "return_data", label: "Dados de Retorno" },
	{ value: "cache", label: "Resgate do Cache" },
];

type CsvExportPayload = {
	id: string;
	status: CsvExportStatus;
	progress_percent: number;
	processed_rows: number;
	total_rows: number;
	error_message: string | null;
	status_url: string;
	download_url: string | null;
};

type CsvExportState = CsvExportPayload & {
	phase: CsvExportPhase;
	fileName: string;
};

const EMPTY_CSV_EXPORT: CsvExportState = {
	id: "",
	status: "pending",
	phase: "idle",
	progress_percent: 0,
	processed_rows: 0,
	total_rows: 0,
	error_message: null,
	status_url: "",
	download_url: null,
	fileName: "",
};

const CSV_EXPORT_POLL_INTERVAL_MS = Number(
	process.env.REACT_APP_CSV_EXPORT_POLL_INTERVAL_MS ?? 3000,
);

function getApiMessage(response: any, fallback: string) {
	const content = getResponseContent<any>(response);
	return (
		content?.message ??
		content?.detail ??
		content?.error_message ??
		response?.data?.detail ??
		fallback
	);
}

function statusToPhase(status: CsvExportStatus): CsvExportPhase {
	if (status === "completed" || status === "error") return status;
	return status === "processing" ? "processing" : "pending";
}

function formatNumber(value?: number | null) {
	return Number(value ?? 0).toLocaleString("pt-BR");
}

// call/list/ nem sempre traz `responses` populado por item — cai pra
// call/retrieve/{id}/ (que sempre traz) só quando necessário.
async function resolveResponsesForQuery(
	query: QueryProps | null,
): Promise<ResponseQueryProps[]> {
	if (!query) return [];
	if (query.responses?.length) return query.responses;

	try {
		const detail = (await api.get(
			`call/retrieve/${query.id}/`,
		)) as RetrieveQueryProps;
		return detail.content.responses ?? [];
	} catch {
		return [];
	}
}

const SEARCH_OPTIONS: SearchFilterOption[] = [
	{
		value: "",
		label: "Todos",
		placeholder: "Placa, código, produto...",
	},
	{
		value: "cpf",
		label: "CPF",
		placeholder: "000.000.000-00",
		normalize: onlyDigits,
		mask: MaskCPF,
		isValid: (term) => term.length === 11,
		invalidMessage: "O CPF deve ter 11 dígitos",
	},
	{
		value: "cnpj",
		label: "CNPJ",
		placeholder: "00.000.000/0000-00",
		normalize: onlyAlphanumeric,
		mask: MaskCNPJ,
		isValid: (term) => term.length === 14,
		invalidMessage: "O CNPJ deve ter 14 caracteres",
	},
	{
		value: "placa",
		label: "Placa / Chassi",
		placeholder: "Placa ou chassi",
		normalize: onlyAlphanumeric,
		isValid: (term) => term.length === 7 || term.length === 17,
		invalidMessage: "A placa deve ter 7 caracteres e o chassi 17",
	},
	{
		value: "insumo",
		label: "Insumo",
		render: ({ term, onTermChange }) => (
			<ApiAutocompleteFilter
				value={term}
				onChange={onTermChange}
				label="Insumo"
				placeholder="Digite o nome do insumo"
				variant="standard"
				fullWidth
			/>
		),
	},
	{
		value: "codigo",
		label: "Código da consulta",
		placeholder: "Somente números",
		normalize: onlyDigits,
		isValid: (term) => term.length === 8,
		invalidMessage: "O código deve ter 8 dígitos",
	},
];

function getSearchTermToSend(type: string, term: string): string | null {
	const trimmed = term.trim();
	if (!trimmed) return "";
	const option = SEARCH_OPTIONS.find((item) => item.value === type);
	if (!isSearchTermValid(option, trimmed)) return null;
	if (!type && trimmed.length < 2) return null;
	return trimmed;
}

export default function Queries(): JSX.Element {
	const { user } = useAuth();
	const navigate = useNavigate();
	const { width } = useWindowSize();
	const {
		setQueries,
		setTitleBar,
		countQueries,
		queriesSearch,
		setCountQueries,
		setPathTitleBar,
		finalDateQueries,
		setQueriesSearch,
		companyIdQueries,
		pageNumberQueries,
		initialDateQueries,
		setFinalDateQueries,
		setCompanyIdQueries,
		setPageNumberQueries,
		setInitialDateQueries,
	} = useCore();
	const isAdmin = user?.profile === "3";
	const [companies, setCompanies] = useState<CompanyProps[] | undefined>([]);
	// const [xlsData, setXlsData] = useState<object>({})
	const [loading, setLoading] = useState<boolean>(true);
	const [csvExport, setCsvExport] = useState<CsvExportState>(EMPTY_CSV_EXPORT);
	const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);
	const [exportDialogError, setExportDialogError] = useState<boolean>(false);
	const [exportCompanyIds, setExportCompanyIds] = useState<string[]>([]);
	const [exportCompanySearch, setExportCompanySearch] = useState<string>("");
	const [exportPinnedCompanyIds, setExportPinnedCompanyIds] = useState<
		string[]
	>([]);
	const [exportReportType, setExportReportType] =
		useState<ExportReportType>("extrato");
	const [exportOptionalColumns, setExportOptionalColumns] = useState<string[]>(
		[],
	);
	const [activeExportReportType, setActiveExportReportType] =
		useState<ExportReportType>("extrato");

	const [resultsVisualizations, setResultsVisualizations] = useState(0);
	const qtdVisualizations = [20, 50, 100];

	const [searchType, setSearchType] = useState<string>("");
	const [searchValue, setSearchValue] = useState<string>("");
	const debouncedSearch = useDebounce(searchValue, 500);
	const lastSearchedRef = useRef(debouncedSearch);
	const requestIdRef = useRef(0);

	const POLL_INTERVAL_MS = Number(
		process.env.REACT_APP_QUERY_HISTORY_PAGE_POLL_INTERVAL_MS ?? 30000,
	);
	const POLL_MAX_TRIES = Number(
		process.env.REACT_APP_QUERY_HISTORY_PAGE_POLL_MAX_TRIES ?? 30,
	);

	const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const pollAttemptsRef = useRef<number>(0);
	const csvExportPollTimerRef = useRef<ReturnType<typeof setInterval> | null>(
		null,
	);
	const csvExportResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	const autoDownloadedExportRef = useRef<string | null>(null);
	const csvExportFileNameRef = useRef<string>("");

	const hasRunningActivity = useCallback(
		() =>
			(queriesSearch ?? []).some(
				(q) => q.get_status_display === "Atividade em execução",
			),
		[queriesSearch],
	);

	// Para evitar múltiplas inicializações
	const didBootstrapRef = useRef<boolean>(false);

	const getQueries = async (page?: number, companyIdOverride?: string) => {
		//setLoading(true)

		const companyId = companyIdOverride ?? companyIdQueries;

		// Validação do intervalo de datas
		if (initialDateQueries && finalDateQueries) {
			const diffMonths = finalDateQueries.diff(initialDateQueries, "months");

			const maxMonths = companyId === "all" ? 5 : 12;

			if (diffMonths > maxMonths) {
				setLoading(false);
				toast.error(
					`Para consultas ${companyId === "all" ? "com todas as empresas" : "de histórico"}, o intervalo máximo permitido é de ${maxMonths} meses.`,
				);
				return;
			}
		}
		let url = `call/list/?initial_date=${initialDateQueries?.toISOString().split("T")[0]}&final_date=${finalDateQueries?.toISOString().split("T")[0]}`;

		if (companyId !== "0") {
			url = `${url}&id=${companyId}`;
		}

		if (page) {
			url = `${url}&page=${page}`;
		}

		if (resultsVisualizations) {
			url = `${url}&page_size=${resultsVisualizations}`;
		}

		const term = getSearchTermToSend(searchType, debouncedSearch);
		if (term) {
			url = `${url}&search=${encodeURIComponent(term)}`;
		}

		const requestId = ++requestIdRef.current;
		const response = (await api.get(url)) as ListQueryProps;
		if (requestId !== requestIdRef.current) return;

		setQueries(response.content.results);
		setQueriesSearch(response.content.results);
		setCountQueries(response.content.count);
		setLoading(false);
	};

	const getCompanies = useCallback(async () => {
		await api.get("company/list/all/?minimal=true").then((response: any) => {
			setCompanies(response.content);
		});
	}, []);

	const handleChangePage = async (
		_event: React.ChangeEvent<unknown>,
		value: number,
	) => {
		setLoading(true);
		setPageNumberQueries(value);
		await getQueries(value);
	};

	const handleCompanyChange = (event: SelectChangeEvent) => {
		setCompanyIdQueries(event.target.value);
	};

	const handleResultsChange = (event: SelectChangeEvent<number>) => {
		setResultsVisualizations(Number(event.target.value));
	};

	const isCsvExportBusy = [
		"starting",
		"pending",
		"processing",
		"downloading",
	].includes(csvExport.phase);
	const showCsvExportProgress = csvExport.phase !== "idle";
	const canDownloadCsvExport =
		csvExport.phase === "completed" && !!csvExport.download_url;

	function clearCsvExportTimers() {
		if (csvExportPollTimerRef.current) {
			clearInterval(csvExportPollTimerRef.current);
			csvExportPollTimerRef.current = null;
		}
		if (csvExportResetTimerRef.current) {
			clearTimeout(csvExportResetTimerRef.current);
			csvExportResetTimerRef.current = null;
		}
	}

	function getCsvExportRequest(
		selectedCompanyIds?: string[],
		reportType: ExportReportType = "extrato",
		optionalColumns: string[] = [],
	) {
		if (!initialDateQueries || !finalDateQueries) {
			toast.error("Informe a data inicial e final para extrair o CSV.", {
				position: toast.POSITION.TOP_RIGHT,
			});
			return null;
		}

		const companyIds =
			selectedCompanyIds ??
			[isAdmin ? companyIdQueries : user?.corporate_id].filter(
				(id): id is string => !!id && id !== "0",
			);
		if (companyIds.length === 0) {
			toast.error("Não foi possível identificar a empresa para extração.", {
				position: toast.POSITION.TOP_RIGHT,
			});
			return null;
		}

		const initialDate = initialDateQueries.toISOString().split("T")[0];
		const finalDate = finalDateQueries.toISOString().split("T")[0];
		const payload: {
			initial_date: string;
			final_date: string;
			company_ids?: string[];
			report_type?: ExportReportType;
			columns?: string[];
		} = {
			initial_date: initialDate,
			final_date: finalDate,
		};
		if (reportType === "faturamento") {
			payload.report_type = reportType;
			payload.columns = optionalColumns;
		}
		const allSelected =
			companyIds[0] === "all" ||
			(companyIds.length > 1 && companyIds.length === (companies?.length ?? 0));

		let url: string;
		let companyName: string;

		if (allSelected) {
			url = "company/download/invoice/all/start/";
			companyName = "Geral";
		} else if (!selectedCompanyIds) {
			url = `company/download/invoice/${companyIds[0]}/start/`;
			companyName =
				companies?.find((value) => value.id === companyIds[0])
					?.corporate_name ?? companyIds[0];
		} else {
			url = "company/download/invoice/all/start/";
			payload.company_ids = companyIds;
			companyName =
				companyIds.length === 1
					? (companies?.find((value) => value.id === companyIds[0])
							?.corporate_name ?? companyIds[0])
					: `${companyIds.length}-empresas`;
		}

		return {
			url,
			payload,
			fileName: `Relatorio_${reportType === "faturamento" ? "Faturamento_" : ""}${companyName}_${initialDate}_${finalDate}.csv`,
		};
	}

	function applyCsvExportPayload(payload: CsvExportPayload, fileName?: string) {
		setCsvExport((previous) => ({
			...previous,
			...payload,
			phase: statusToPhase(payload.status),
			fileName: fileName ?? previous.fileName,
			progress_percent: Math.min(
				Math.max(payload.progress_percent ?? 0, 0),
				100,
			),
			processed_rows: payload.processed_rows ?? 0,
			total_rows: payload.total_rows ?? 0,
			error_message: payload.error_message ?? null,
			download_url: payload.download_url ?? null,
		}));
	}

	function startCsvExportPolling(exportId: string) {
		if (csvExportPollTimerRef.current) return;

		csvExportPollTimerRef.current = setInterval(() => {
			refreshCsvExportStatus(exportId).catch((error) => {
				console.error("CSV export polling error", error);
				clearCsvExportTimers();
				setCsvExport((previous) => ({
					...previous,
					phase: "error",
					status: "error",
					error_message: "Erro ao consultar o status da planilha.",
				}));
			});
		}, CSV_EXPORT_POLL_INTERVAL_MS);
	}

	function openExportDialog(reportType: ExportReportType) {
		const prefilledCompanyIds =
			companyIdQueries === "all"
				? (companies ?? []).map((company) => company.id)
				: [companyIdQueries].filter((id) => id !== "0");
		setExportReportType(reportType);
		setExportCompanyIds(prefilledCompanyIds);
		setExportPinnedCompanyIds(prefilledCompanyIds);
		setExportOptionalColumns([]);
		setExportCompanySearch("");
		setExportDialogError(false);
		setExportDialogOpen(true);
	}

	async function startCsvExport(
		selectedCompanyIds?: string[],
		reportType: ExportReportType = "extrato",
		optionalColumns: string[] = [],
	) {
		const request = getCsvExportRequest(
			selectedCompanyIds,
			reportType,
			optionalColumns,
		);
		if (!request) return;

		clearCsvExportTimers();
		setActiveExportReportType(reportType);
		autoDownloadedExportRef.current = null;
		csvExportFileNameRef.current = request.fileName;
		setCsvExport({
			...EMPTY_CSV_EXPORT,
			phase: "starting",
			fileName: request.fileName,
		});

		try {
			const response = (await api.post(request.url, request.payload)) as any;

			if (response?.status >= 400) {
				throw new Error(
					getApiMessage(response, "Erro ao iniciar a geração da planilha."),
				);
			}

			const payload = getResponseContent<CsvExportPayload>(response);
			if (!payload?.id) {
				throw new Error("A API não retornou o identificador da exportação.");
			}

			applyCsvExportPayload(payload, request.fileName);

			if (payload.status === "completed" && payload.download_url) {
				await downloadCsvExport(payload, request.fileName, true);
				return;
			}

			if (payload.status === "error") {
				throw new Error(payload.error_message ?? "Erro ao gerar a planilha.");
			}

			startCsvExportPolling(payload.id);
		} catch (error: any) {
			clearCsvExportTimers();
			setCsvExport((previous) => ({
				...previous,
				phase: "error",
				status: "error",
				error_message:
					error?.message ?? "Erro ao iniciar a geração da planilha.",
			}));
			toast.error(error?.message ?? "Erro ao iniciar a geração da planilha.", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	}

	async function refreshCsvExportStatus(exportId: string) {
		const response = (await api.get(
			`company/download/invoice/export/${exportId}/status/`,
		)) as any;
		const payload = getResponseContent<CsvExportPayload>(response);

		if (!payload?.id) {
			throw new Error(
				getApiMessage(response, "Status da exportação não encontrado."),
			);
		}

		applyCsvExportPayload(payload);

		if (payload.status === "completed" && payload.download_url) {
			clearCsvExportTimers();

			if (autoDownloadedExportRef.current !== payload.id) {
				autoDownloadedExportRef.current = payload.id;
				await downloadCsvExport(
					payload,
					csvExportFileNameRef.current || csvExport.fileName,
					true,
				);
			}
			return;
		}

		if (payload.status === "error") {
			clearCsvExportTimers();
			toast.error(payload.error_message ?? "Erro ao gerar a planilha.", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	}

	async function downloadCsvExport(
		payload: CsvExportPayload | CsvExportState = csvExport,
		fileName = csvExport.fileName,
		automatic = false,
	) {
		if (!payload.id) return;

		try {
			setCsvExport((previous) => ({
				...previous,
				phase: "downloading",
			}));

			const response = (await api.get(
				`company/download/invoice/export/${payload.id}/file/`,
			)) as any;
			const downloadUrl = getResponseContent<{ download_url?: string }>(
				response,
			)?.download_url;
			if (!downloadUrl) {
				throw new Error(
					getApiMessage(
						response,
						"A API não retornou uma URL de download válida.",
					),
				);
			}

			window.location.href = downloadUrl;
			setCsvExport((previous) => ({
				...previous,
				phase: "completed",
				status: "completed",
				progress_percent: 100,
			}));

			if (automatic) {
				csvExportResetTimerRef.current = setTimeout(() => {
					setCsvExport(EMPTY_CSV_EXPORT);
				}, 8000);
			}
		} catch (error: any) {
			setCsvExport((previous) => ({
				...previous,
				phase: "completed",
				status: "completed",
				error_message: null,
			}));
			toast.error(error?.message ?? "Erro ao baixar a planilha.", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	}

	const exportCompanyTerm = exportCompanySearch.trim().toLowerCase();
	const filteredExportCompanies = (companies ?? [])
		.filter((company) =>
			company.corporate_name.toLowerCase().includes(exportCompanyTerm),
		)
		.sort(
			(a, b) =>
				Number(exportPinnedCompanyIds.includes(b.id)) -
				Number(exportPinnedCompanyIds.includes(a.id)),
		);
	const allExportCompaniesSelected =
		!!companies?.length && exportCompanyIds.length === companies.length;

	function toggleExportCompany(companyId: string) {
		setExportDialogError(false);
		setExportCompanyIds((previous) =>
			previous.includes(companyId)
				? previous.filter((id) => id !== companyId)
				: [...previous, companyId],
		);
	}

	function toggleAllExportCompanies() {
		setExportDialogError(false);
		setExportCompanyIds(
			allExportCompaniesSelected
				? []
				: (companies ?? []).map((company) => company.id),
		);
	}

	function toggleExportOptionalColumn(column: string) {
		setExportOptionalColumns((previous) =>
			previous.includes(column)
				? previous.filter((value) => value !== column)
				: [...previous, column],
		);
	}

	function confirmExportDialog() {
		if (exportCompanyIds.length === 0) {
			setExportDialogError(true);
			return;
		}
		setExportDialogOpen(false);
		const orderedOptionalColumns = BILLING_OPTIONAL_COLUMNS.map(
			(column) => column.value,
		).filter((column) => exportOptionalColumns.includes(column));
		startCsvExport(exportCompanyIds, exportReportType, orderedOptionalColumns);
	}

	function getCsvExportStatusText() {
		if (csvExport.phase === "starting")
			return "Solicitando geração da planilha...";
		if (csvExport.phase === "pending") return "Planilha na fila de geração...";
		if (csvExport.phase === "processing") return "Gerando planilha...";
		if (csvExport.phase === "downloading")
			return "Planilha pronta. Iniciando download...";
		if (csvExport.phase === "completed")
			return "Planilha pronta para download.";
		if (csvExport.phase === "error")
			return csvExport.error_message ?? "Erro ao gerar a planilha.";
		return "";
	}

	useLayoutEffect(() => {
		setPathTitleBar("/");
		setTitleBar("Minhas consultas");
	}, []);

	useEffect(() => {
		getCompanies();

		if (didBootstrapRef.current) return;
		didBootstrapRef.current = true;

		const bootstrap = async () => {
			if (user?.corporate_id) {
				const companyId = String(user.corporate_id);
				setCompanyIdQueries(companyId);
				await getQueries(undefined, companyId).catch(console.error);
				return;
			}

			if (queriesSearch!.length > 0) {
				setLoading(false);
			} else {
				await getQueries().catch(console.error);
			}
		};

		bootstrap().catch(console.error);

		// Cleanup: garante que o polling seja limpo ao desmontar a página
		return () => {
			if (pollTimerRef.current) clearInterval(pollTimerRef.current);
			pollTimerRef.current = null;
			clearCsvExportTimers();
		};
	}, []);

	useEffect(() => {
		if (lastSearchedRef.current === debouncedSearch) return;
		lastSearchedRef.current = debouncedSearch;

		if (getSearchTermToSend(searchType, debouncedSearch) === null) return;
		setLoading(true);
		setPageNumberQueries(1);
		getQueries().catch(console.error);
	}, [debouncedSearch]);

	// Polling automático enquanto houver "Atividade em execução"
	useEffect(() => {
		if (pollTimerRef.current && !hasRunningActivity()) {
			clearInterval(pollTimerRef.current);
			pollTimerRef.current = null;
			pollAttemptsRef.current = 0;
		}

		// Se NÃO há item em execução, não inicia polling.
		if (!hasRunningActivity()) return;

		// Se já está rodando um timer, evita duplicar.
		if (pollTimerRef.current) return;

		// Inicia polling
		pollAttemptsRef.current = 0;
		pollTimerRef.current = setInterval(async () => {
			// Limita tentativas
			if (pollAttemptsRef.current >= POLL_MAX_TRIES) {
				if (pollTimerRef.current) clearInterval(pollTimerRef.current);
				pollTimerRef.current = null;
				return;
			}

			pollAttemptsRef.current += 1;

			try {
				// Atualiza a listagem mantendo filtros/página atuais
				await getQueries(pageNumberQueries);
			} catch (e) {
				// Em erro, apenas continua tentando até o limite
				console.error("Polling getQueries error", e);
			}

			// Se após atualizar não há mais "Atividade em execução", encerra
			if (!hasRunningActivity() && pollTimerRef.current) {
				clearInterval(pollTimerRef.current);
				pollTimerRef.current = null;
				pollAttemptsRef.current = 0;
			}
		}, POLL_INTERVAL_MS);

		// Cleanup defensivo quando filtros/condições mudam
		return () => {
			if (pollTimerRef.current) {
				clearInterval(pollTimerRef.current);
				pollTimerRef.current = null;
			}
		};
	}, [hasRunningActivity, pageNumberQueries, POLL_INTERVAL_MS, POLL_MAX_TRIES]);

	return (
		<>
			{/* filtros */}
			<Grid container gap={2} alignItems="spaceAround" textAlign="center">
				{user?.profile === "3" && (
					<FormControl sx={{ marginTop: "9px" }}>
						<Select
							required
							labelId="company-label"
							id="company"
							name="company"
							value={companyIdQueries}
							onChange={handleCompanyChange}
							disabled={loading}
						>
							<MenuItem key={`company-none`} value="0">
								Selecione a empresa
							</MenuItem>
							<MenuItem key={`company-all`} value="all">
								Todas as empresas
							</MenuItem>
							{companies?.map((c) => (
								<MenuItem key={`company-${c.id}`} value={c.id}>
									{c.corporate_name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}

				<InputDatePicker
					value={initialDateQueries}
					setValue={setInitialDateQueries}
				/>
				<InputDatePicker
					value={finalDateQueries}
					setValue={setFinalDateQueries}
				/>

				<Select
					sx={{ marginTop: "9px" }}
					required
					labelId="results-label"
					id="results"
					name="results"
					value={resultsVisualizations}
					onChange={handleResultsChange}
					disabled={loading}
				>
					<MenuItem key={`results-none`} value={0}>
						Qtd. Resultados
					</MenuItem>
					{qtdVisualizations?.map((value) => (
						<MenuItem key={`results-${value}`} value={value}>
							{value}
						</MenuItem>
					))}
				</Select>

				<Button
					variant="contained"
					size="large"
					sx={{ marginTop: "9px" }}
					onClick={() => {
						setLoading(true);
						setPageNumberQueries(1);
						getQueries().catch(console.error);
					}}
					disabled={loading}
					color={"success"}
				>
					Buscar
				</Button>

				{queriesSearch!.length > 0 && user?.profile !== "1" && (
					<Button
						variant="outlined"
						size="large"
						sx={{ marginTop: "9px" }}
						onClick={() =>
							isAdmin ? openExportDialog("extrato") : startCsvExport()
						}
						disabled={loading || isCsvExportBusy}
						color={"success"}
					>
						{isCsvExportBusy && activeExportReportType === "extrato"
							? "Gerando CSV"
							: "Extrair CSV"}
					</Button>
				)}

				{queriesSearch!.length > 0 && isAdmin && (
					<Button
						variant="outlined"
						size="large"
						sx={{ marginTop: "9px" }}
						onClick={() => openExportDialog("faturamento")}
						disabled={loading || isCsvExportBusy}
						color={"success"}
					>
						{isCsvExportBusy && activeExportReportType === "faturamento"
							? "Gerando faturamento"
							: "Extrair faturamento"}
					</Button>
				)}

				{canDownloadCsvExport && (
					<Button
						variant="contained"
						size="large"
						sx={{ marginTop: "9px" }}
						onClick={() => downloadCsvExport()}
						color={"success"}
					>
						Baixar CSV
					</Button>
				)}
			</Grid>

			{showCsvExportProgress && (
				<Alert
					severity={
						csvExport.phase === "error"
							? "error"
							: csvExport.phase === "completed"
								? "success"
								: "info"
					}
					variant="outlined"
					sx={{ mt: 2 }}
				>
					<Box sx={{ width: "100%" }}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								gap: 2,
								mb: 1,
								flexWrap: "wrap",
							}}
						>
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								{getCsvExportStatusText()}
							</Typography>
							<Typography variant="body2">
								{csvExport.progress_percent}% concluído
							</Typography>
						</Box>

						<LinearProgress
							variant="determinate"
							value={
								csvExport.phase === "error" ? 100 : csvExport.progress_percent
							}
							color={csvExport.phase === "error" ? "error" : "success"}
							sx={{ height: 8, borderRadius: 1 }}
						/>

						<Typography variant="caption" sx={{ display: "block", mt: 1 }}>
							{csvExport.total_rows > 0
								? `${formatNumber(csvExport.processed_rows)} de ${formatNumber(csvExport.total_rows)} linhas processadas`
								: `${formatNumber(csvExport.processed_rows)} linhas processadas`}
						</Typography>
					</Box>
				</Alert>
			)}

			<Dialog
				open={exportDialogOpen}
				onClose={() => setExportDialogOpen(false)}
				fullWidth
				maxWidth="xs"
				aria-labelledby="export-companies-title"
			>
				<DialogTitle id="export-companies-title">
					{exportReportType === "faturamento"
						? "Extrato de faturamento"
						: "Extrato por empresa"}
				</DialogTitle>
				<DialogContent dividers>
					<TextField
						fullWidth
						size="small"
						label="Buscar empresa"
						value={exportCompanySearch}
						onChange={(event) => setExportCompanySearch(event.target.value)}
						sx={{ mb: 1 }}
					/>
					{!exportCompanyTerm && (
						<FormControlLabel
							sx={{ display: "flex" }}
							label="Selecionar todas"
							control={
								<Checkbox
									checked={allExportCompaniesSelected}
									indeterminate={
										exportCompanyIds.length > 0 && !allExportCompaniesSelected
									}
									onChange={toggleAllExportCompanies}
								/>
							}
						/>
					)}
					<Box sx={{ maxHeight: 320, overflowY: "auto" }}>
						{filteredExportCompanies.map((company) => (
							<FormControlLabel
								key={company.id}
								sx={{ display: "flex" }}
								label={company.corporate_name}
								control={
									<Checkbox
										checked={exportCompanyIds.includes(company.id)}
										onChange={() => toggleExportCompany(company.id)}
									/>
								}
							/>
						))}
						{filteredExportCompanies.length === 0 && (
							<Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
								Nenhuma empresa encontrada
							</Typography>
						)}
					</Box>
					{exportReportType === "faturamento" && (
						<Box sx={{ mt: 2 }}>
							<Typography variant="subtitle2">Colunas opcionais</Typography>
							{BILLING_OPTIONAL_COLUMNS.map((column) => (
								<FormControlLabel
									key={column.value}
									sx={{ display: "flex" }}
									label={column.label}
									control={
										<Checkbox
											checked={exportOptionalColumns.includes(column.value)}
											onChange={() => toggleExportOptionalColumn(column.value)}
										/>
									}
								/>
							))}
						</Box>
					)}
					{exportDialogError && (
						<Alert severity="warning" sx={{ mt: 2 }}>
							Selecione ao menos uma empresa para gerar o extrato.
						</Alert>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setExportDialogOpen(false)} color="inherit">
						Cancelar
					</Button>
					<Button
						onClick={confirmExportDialog}
						variant="contained"
						color="success"
					>
						Gerar CSV
					</Button>
				</DialogActions>
			</Dialog>

			<br />

			{/* lista de consultas */}
			<List dense disablePadding sx={{ width: "100%" }}>
				<ListItem key="search" disablePadding>
					<Box sx={{ width: "100%", m: 1 }}>
						<SearchWithFilter
							options={SEARCH_OPTIONS}
							type={searchType}
							term={searchValue}
							onTypeChange={setSearchType}
							onTermChange={setSearchValue}
						/>
					</Box>
				</ListItem>

				{loading ? (
					<ListItem disablePadding>
						<ListItemAvatar>
							<CircularProgress size={24} />
						</ListItemAvatar>
						<ListItemText primary="Carregando consultas…" />
					</ListItem>
				) : queriesSearch!.length > 0 ? (
					queriesSearch?.map((item: QueryProps) => {
						const isErrorStatus = item.status === "4"; // Atividade finalizada com erro
						const isDisabled = !isAdmin && isErrorStatus;
						const statusLabel =
							item.status === "5"
								? "Atividade com informações indisponíveis"
								: item.get_status_display;

						const rawParams = Object.values(
							(item.data ?? {}) as Record<
								string,
								{ value: unknown; description?: string; value_type?: string }
							>,
						);

						// pega só valores de texto/número e remove vazios
						const uniqueParamValues = Array.from(
							new Set(
								rawParams
									.map((p) =>
										typeof p.value === "string" || typeof p.value === "number"
											? String(p.value).trim().slice(0, 22)
											: "",
									)
									.filter(Boolean),
							),
						);

						let renderedParams = "";

						if (uniqueParamValues.length === 1) {
							renderedParams = `Parâmetro: ${uniqueParamValues[0]}`;
						} else if (uniqueParamValues.length > 1) {
							renderedParams = `Parâmetros: ${uniqueParamValues.join(" | ")}`;
						}

						return (
							<ListItem
								key={item.id}
								disablePadding
								sx={
									width < 1080
										? { flexDirection: "column", alignItems: "stretch" }
										: undefined
								}
								secondaryAction={
									width >= 1080 ? (
										<Box display="flex" alignItems="center" gap={1}>
											<Chip
												label={statusLabel}
												sx={{
													backgroundColor: StatusColor[item.status],
													color: colors.common.white,
												}}
											/>
											{isAdmin && (
												<ResponseButton
													variant="icon"
													edge="end"
													getResponses={() => resolveResponsesForQuery(item)}
												/>
											)}
										</Box>
									) : null
								}
							>
								<ListItemButton
									disabled={isDisabled}
									onClick={() => {
										if (!isDisabled) {
											navigate(`/query/${item.id}`);
										}
									}}
								>
									<ListItemAvatar>
										<Avatar sx={{ backgroundColor: StatusColor[item.status] }}>
											{item.checkmatch ? (
												<BsUiChecks />
											) : (
												<ContentPasteSearchIcon />
											)}
										</Avatar>
									</ListItemAvatar>

									<ListItemText
										primary={
											item.products.length > 1 ? (
												<Tooltip
													title={`${item.products.map((item) => ` ${item}`)}`}
													arrow
													placement="right"
												>
													<span>Consulta múltipla</span>
												</Tooltip>
											) : (
												`${item.products.map((item) => ` ${item}`)}`
											)
										}
										secondary={
											<>
												{item.query_code && (
													<>
														Código: {item.query_code}
														<br />
													</>
												)}
												{renderedParams}
												<br />
												{item.created_at}
												{width < 1080 && (
													<>
														<br />
														<span style={{ color: StatusColor[item.status] }}>
															{statusLabel}
														</span>
													</>
												)}
											</>
										}
									/>
								</ListItemButton>

								{isAdmin && width < 1080 && (
									<Box
										display="flex"
										justifyContent="flex-end"
										gap={1}
										sx={{ px: 2, pb: 1 }}
									>
										<ResponseButton
											variant="icon"
											getResponses={() => resolveResponsesForQuery(item)}
										/>
									</Box>
								)}
							</ListItem>
						);
					})
				) : (
					<ListItem disablePadding>
						<Alert
							severity="info"
							variant="filled"
							sx={{ width: "100%", textAlign: "center", color: "#ffffff" }}
						>
							Nada encontrado
						</Alert>
					</ListItem>
				)}
			</List>

			{/* paginação sempre visível */}
			<Paginator
				count={countQueries}
				pageNumber={pageNumberQueries}
				qtdResults={resultsVisualizations}
				onChange={handleChangePage}
			/>
		</>
	);
}
